import React from "react";
import * as Icon from "react-feather";
import UserService from "../../services/UserService";
import CommunicationService from "../../services/CommunicationService";
import TextFieldInput from "../common/text_field_input/TextFieldInput";
import UtilityService from "../../services/UtilityService";

import StaffService from "../../services/StaffService";
import BackofficeService from "../../services/BackofficeService";
import WComponent from "../common/WComponent";
import SearchableDropdown from "../common/searchable_dropdown/SearchableDropdown";
import Button from "../common/button/Button";
import PageHeading from "../common/page_heading/PageHeading";

import "./settings.css";
import "./staff-settings.css";
import PermissionGate from "../../permissions/permissionGate";
import RestrictedAccess from "../common/restricted_access/RestrictedAccess";
import RequiredLabel from "../common/required_label";

const { alert } = UtilityService;

class StaffSettings extends WComponent {
	constructor() {
		super();

		this.state = {
			loading: true,

			firstName: "",
			lastName: "",
			title: "",
			email: "",
			phone: "",
			password: "",
			confirmPassword: "",
			status: "",
			defaultRoleId: 0,
			defaultRoleName: "",
			isNotAllowedResource: false,

			googleConnected: false,
			googleEmail: null,

			backofficeWeeklyHours: "",
			backofficeWeeklyHoursLoaded: "",
		};
	}

	componentDidMount = () => {
		this.resetComponent();
	};

	componentDidUpdate = prevProps => {
		let { userId } = this.props.match.params;

		if (prevProps.match.params.userId !== userId) {
			this.resetComponent();
		}
	};

	resetComponent = async () => {
		let { userId } = this.props.match.params;

		await this.update({
			userId: userId
		});

		await this.update({
			loading: true
		});

		if (userId === "new") {
			await this.update({
				loading: false
			});
			return;
		}

		await this.fetchUser();
	};

	isOwnProfile = () => {
		let { userId } = this.state;
		let { userId: currentUserId } = UserService.getUserData();
		return parseInt(userId) === parseInt(currentUserId);
	};

	fetchGoogleStatus = async () => {
		const status = await CommunicationService.getGoogleStatus();
		if (status) {
			await this.update({ googleConnected: status.connected, googleEmail: status.email || null });
		}
	};

	onConnectGoogle = async () => {
		const url = await CommunicationService.getGoogleAuthUrl();
		if (url) window.location.href = url;
	};

	onDisconnectGoogle = async () => {
		const ok = await CommunicationService.disconnectGoogle();
		if (ok) {
			await this.update({ googleConnected: false, googleEmail: null });
			alert({ type: "success", text: "Google account disconnected." });
		} else {
			alert({ type: "error", text: "Could not disconnect Google account. Please try again." });
		}
	};

	fetchUser = async () => {
		let { userId } = this.state;

		let user = await UserService.fetchUser({ userId });

		if(UtilityService.isNotAllowedResource({ response: user })) {
			await this.update({
				isNotAllowedResource: true
			});
			return;
		}

		await this.update({
			loading: false
		});

		if (!user) {
			alert({ type: "error", text: "User does not exist." });
			this.props.history.push("/settings/company");
			return;
		}

		await this.update({
			firstName: user.first_name,
			lastName: user.last_name,
			title: user.title,
			email: user.email,
			phone: user.phone,
			status: user.status,
			defaultRoleId: UtilityService.isNullish(user.defaultRole) ? undefined : user.defaultRole.id,
			defaultRoleName: UtilityService.isNullish(user.defaultRole) ? undefined : user.defaultRole.name
		});

		if (this.isOwnProfile()) {
			this.fetchGoogleStatus();
		}

		this.fetchBackofficeCapacity();
	};

	fetchBackofficeCapacity = async () => {
		let { userId } = this.state;
		let result = await BackofficeService.listCapacity();
		if (!result.ok) return;
		let row = result.data.find(u => String(u.id) === String(userId));
		let hours = row && row.weeklyHours !== null && row.weeklyHours !== undefined ? String(row.weeklyHours) : "";
		await this.update({ backofficeWeeklyHours: hours, backofficeWeeklyHoursLoaded: hours });
	};

	saveBackofficeCapacity = async () => {
		let { userId, backofficeWeeklyHours, backofficeWeeklyHoursLoaded } = this.state;
		if (backofficeWeeklyHours === backofficeWeeklyHoursLoaded) return;
		let result = await BackofficeService.updateCapacity({ userId, weeklyHours: parseFloat(backofficeWeeklyHours) || 0 });
		if (result.ok) await this.update({ backofficeWeeklyHoursLoaded: backofficeWeeklyHours });
		else alert({ type: "error", text: "Backoffice capacity was not saved: " + result.error });
	};

	updateUser = async () => {
		let { userId, firstName, lastName, title, email, phone, status, password, defaultRoleId } = this.state;

		const missing = this.getMissingRequiredFields();
		const errors = this.getValidationErrors();
		if (missing.length || errors.length) {
			const parts = [];
			if (missing.length) parts.push(`Please fill: ${missing.join(", ")}`);
			if (errors.length) parts.push(errors.join(". "));
			alert({ type: "error", text: parts.join(". ") });
			return;
		}

		if (this.state.loading) {
			alert({ type: "info", text: "Updating..." });
			return;
		}

		try {
			await StaffService.update({
				userId: userId,
				firstName: firstName,
				lastName: lastName,
				password: password,
				title: title,
				email: email,
				phone: phone,
				status: status,
				defaultRoleId: defaultRoleId
			});

			await this.saveBackofficeCapacity();

			alert({ type: "success", text: "Updated Successfully!" });
		} catch (error) {
			alert({ type: "error", text: "Update Error!" });
			console.log(error);
		}

		await this.update({
			loading: false
		});
	};

	createUser = async () => {
		let { history } = this.props;
		let { firstName, lastName, title, email, phone, password, defaultRoleId } = this.state;

		const missing = this.getMissingRequiredFields();
		const errors = this.getValidationErrors();
		if (missing.length || errors.length) {
			const parts = [];
			if (missing.length) parts.push(`Please fill: ${missing.join(", ")}`);
			if (errors.length) parts.push(errors.join(". "));
			alert({ type: "error", text: parts.join(". ") });
			return;
		}

		if (this.state.loading) {
			// Call alert saying the client is being updated
			alert({ type: "info", text: "Updating..." });
			return;
		}

		this.update({
			loading: true
		});

		try {
			let response = await StaffService.create({ firstName, lastName, title, email, phone, password, defaultRoleId });

			// Call alert saying that the client was updated successfully
			alert({ type: "success", text: "Created Successfully!" });
			history.push(`/settings/users/${response.data.id}`);
		} catch (error) {
			console.log(error);
			// Call alert saying there is some kind of error trying to update the client
			alert({ type: "error", text: "Update Error!" });
		}

		this.update({
			loading: false
		});
	};

	archiveUser = async () => {
		let { loading, status, userId } = this.state;

		if (!this.isValid()) {
			alert({ type: "error", text: "Missing fields!" });
			return;
		}

		if (loading) {
			alert({ type: "info", text: "Updating..." });
			return;
		}

		let newStatus = status === "active" ? "inactive" : "active";

		try {
			await StaffService.update({ userId, status: newStatus });

			alert({ type: "success", text: "Updated Successfully!" });

			await this.update({
				status: newStatus
			});
		} catch (error) {
			alert({ type: "error", text: "Update Error!" });
			console.log(error);
		}

		this.update({
			loading: false
		});
	};

	onChange = e => {
		let { name, value } = e.target;

		this.update({
			[name]: value
		});
	};

	onTitleSelect = title => {
		this.update({
			title: title.key
		});
	};

	onSelectDefaultRole = role => {
		this.update({
			defaultRoleId: role.id,
			defaultRoleName: role.name
		});
	};

	onBack = () => {
		let { history } = this.props;
		history.goBack();
	};

	isNewUser = () => {
		let { userId } = this.state;
		return userId === "new";
	};

	isDisabled = () => {
		let { loading } = this.state;
		return !this.isValid() || loading;
	};

	isPasswordCorrectLength = () => {
		let { password } = this.state;

		return password.length >= 8;
	};

	isPasswordMatching = () => {
		let { password, confirmPassword } = this.state;
		return password === confirmPassword && password.length >= 8;
	};

	isValid = () => {
		const missing = this.getMissingRequiredFields();
		const errors = this.getValidationErrors();
		return missing.length === 0 && errors.length === 0;
	};

	getMissingRequiredFields = () => {
		const { firstName, lastName, email,defaultRoleId, password, confirmPassword } = this.state;
		const missing = [];

		if (!firstName?.trim()) missing.push("First Name");
		if (!lastName?.trim()) missing.push("Last Name");
		if (!email?.trim()) missing.push("Email");
		if (!defaultRoleId) missing.push("Default Role");
		// For NEW user, password fields are required
		if (this.isNewUser()) {
			if (!password?.trim()) missing.push("Password");
			if (!confirmPassword?.trim()) missing.push("Confirm Password");
		}

		return missing;
	};

	getValidationErrors = () => {
		const { password, confirmPassword } = this.state;
		const errors = [];

		if (password?.length || confirmPassword?.length) {
			if (password !== confirmPassword) errors.push("Passwords do not match");
			if ((password || "").length < 8) errors.push("Password must be at least 8 characters");
		}

		if (this.isNewUser() && password?.length && confirmPassword?.length) {
			if (password !== confirmPassword) errors.push("Passwords do not match");
			if (password.length < 8) errors.push("Password must be at least 8 characters");
		}

		return errors;
	};

	render() {
		let { firstName, lastName, title, email, phone, password, confirmPassword, status, defaultRoleName, isNotAllowedResource, googleConnected, googleEmail } = this.state;

		if (isNotAllowedResource) {
			return <RestrictedAccess />
		}

		return (
			<div className="staff-settings">
				<PageHeading section="Settings" title={"Edit Profile"} />

				<div className="staff-settings__form">
					<div>
						<TextFieldInput
							containerClass="staff-settings__form__input"
							title={<RequiredLabel text="First Name" />}
							name="firstName"
							value={firstName || ""}
							onChange={this.onChange}
							autoComplete="off"
							className="ft-user-form-firstname"
						/>

						<TextFieldInput
							containerClass="staff-settings__form__input"
							title={<RequiredLabel text="Last Name" />}
							name="lastName"
							value={lastName || ""}
							onChange={this.onChange}
							autoComplete="off"
							className="ft-user-form-lastname"							
						/>

						<TextFieldInput
							containerClass="staff-settings__form__input"
							title="Title"
							name="title"
							value={title || ""}
							onChange={this.onChange}
							autoComplete="off"
							className="ft-user-form-title"							
						/>

						<SearchableDropdown
							containerClass="staff-settings__form__input"
							title={<RequiredLabel text="Default Role" />}
							type={"roles"}
							selectedName={defaultRoleName || ""}
							onSelect={this.onSelectDefaultRole}
							autoComplete="off"
							className="ft-user-form-role"							
						/>

						{/*<div className="contact-section">
                                <div>
                                    <h5>Preferred Contact Medium</h5>
                                </div>

                                <div className="contact-buttons">
                                    <Link to="#" style={{ textDecoration: 'none'}}>
                                        <input type="submit" value="Email" id="email-button"/>
                                    </Link>
                                    <Link to="#" style={{ textDecoration: 'none' }}>
                                        <input type="submit" value="SMS" id="sms-button"/>
                                    </Link>
                                </div>
                            </div>*/}
					</div>
					<div className="staff-settings-container-2">
						<TextFieldInput
							containerClass="staff-settings__form__input"
							disabled={!UserService.isAdministrator()}
							title={<RequiredLabel text="Email" />}
							name="email"
							value={email || ""}
							onChange={this.onChange}
							autoComplete="off"
							className="ft-user-form-email"							
						/>

						<TextFieldInput
							containerClass="staff-settings__form__input"
							title="Phone"
							name="phone"
							value={phone || ""}
							onChange={this.onChange}
							autoComplete="off"
							className="ft-user-form-phone"							
						/>

						<TextFieldInput
							containerClass="staff-settings__form__input"
							title={<RequiredLabel text="Password" />}
							type="password"
							name="password"
							value={password}
							onChange={this.onChange}
							autoComplete="off"
							className="ft-user-form-password"							
						/>

						<TextFieldInput
							containerClass="staff-settings__form__input"
							title={<RequiredLabel text="Confirm Password" />}
							type="password"
							name="confirmPassword"
							value={confirmPassword}
							onChange={this.onChange}
							autoComplete="off"
							className="ft-user-form-confirmpassword"							
						/>

						<div className="user-password">{this.isPasswordMatching() ? "Password Matches" : ""}</div>
					</div>
					{!this.isNewUser() && (
						<TextFieldInput
							title="Weekly backoffice capacity (hours)"
							name="backofficeWeeklyHours"
							type="number"
							value={this.state.backofficeWeeklyHours}
							onChange={e => this.update({ backofficeWeeklyHours: e.target.value })}
							placeholder="e.g. 10"
							autoComplete="off"
							className="ft-user-form-backoffice-hours"
						/>
					)}
					<div className="staff-settings__form__footer">
						<PermissionGate resource="user" action="profileArchiveAction">
							{!this.isNewUser() && <Button view="secondary" text={status === "active" ? "Archive" : "Unarchive"} onClick={this.archiveUser} className="ft-user-archivebtn"/>}
						</PermissionGate>
						<PermissionGate resource="user" action="create">
							{this.isNewUser() && <Button text="Create" onClick={this.createUser} className="ft-user-createbtn" />}
						</PermissionGate>
						<PermissionGate resource="user" action="update">
							{!this.isNewUser() && <Button text="Update" onClick={this.updateUser} className="ft-user-updatebtn" />}
						</PermissionGate>
					</div>

					{!this.isNewUser() && this.isOwnProfile() && (
						<div className="staff-settings__google">
							<div className="staff-settings__google__label">Google Account</div>
							{googleConnected ? (
								<div className="staff-settings__google__connected">
									<div className="staff-settings__google__connected__email">
										<Icon.Mail size={18} />
										<span>{googleEmail}</span>
									</div>
									<Button view="secondary" text="Disconnect" onClick={this.onDisconnectGoogle} />
								</div>
							) : (
								<div className="staff-settings__google__disconnected">
									<span>Connect your Google account to schedule and sync Meet meetings.</span>
									<Button text="Connect Google" onClick={this.onConnectGoogle} />
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		);
	}
}

export default StaffSettings;
